import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Copy, Download, Puzzle } from 'lucide-react';
import { create } from 'zustand';
import { api } from '../services/api';
import { useAuthStore } from '../store/authStore';
import Modal from './Modal';
import PendingDecision from './ui/PendingDecision';
import {
  CHROME_EXTENSIONS_URL, installMode, installState, mergeExtensionIds, needsHandoff, showUpdateBar, type ExtensionKind, type ExtensionPing, type InstallState,
} from '../utils/extensionInstallModel';

/**
 * P-63 (D-239): installing the OTE browser extension.
 *
 * C-30 (clarity, N-04): no permanent button in the top bar any more. This host
 * (mounted once in `Layout`, under the bar) shows a ONE-TIME notice with
 * "התקנה" and "לא עכשיו" (remembered per user and per state in localStorage:
 * a new version shows its notice once again), and publishes the state to
 * `useExtensionInstallStore`, which puts "התקנת התוסף" / "עדכון התוסף" /
 * "התוסף מותקן" in the user menu (`ui/AvatarMenu`, the mobile "עוד" sheet).
 * The detection and hand-off logic (`extensionInstallModel`) is unchanged:
 *
 * - The server decides which extension this user may install (staff -> employee, everyone else
 *   -> customer); the other side's extension is never shown or linked.
 * - It asks the extension whether it is there (`ping` over externally_connectable). Installed:
 *   the menu says so (with the version); outdated: "update available"; otherwise the install item.
 * - Installed but signed out (or signed in as someone else): the already signed-in page asks the
 *   server for a ONE-MINUTE, single-use token and hands only that to the extension. The site's
 *   long-lived JWT is never passed.
 * - Install source (D-240): the zip + a 5-step illustrated guide by default; a Chrome Web Store link only
 *   when the server has CHROME_STORE_URL_* configured (a config switch for later). An outdated zip install
 *   shows replace-and-reload instructions.
 */
const BUILTIN_EXTENSION_IDS: Record<ExtensionKind, string> = {
  employee: 'fmakeekdojalaibkmmdofjgebinjfkfd',
  // P-64 (D-197): the customer extension's fixed id (manifest.customer.json `key`); VITE_EXT_ID_CUSTOMER may override it.
  customer: (import.meta.env as Record<string, string | undefined>).VITE_EXT_ID_CUSTOMER || 'kemebdegehbhnjdhjdhccndhichcgpfc',
};

/**
 * D-345: the Chrome Web Store build has its own id, so a kind may have several. The built-in id stays; the server
 * (`/extension/info` -> `extension_ids`, from System Settings) adds the store id without a new site build.
 */
const knownExtensionIds: Record<ExtensionKind, string[]> = {
  employee: [BUILTIN_EXTENSION_IDS.employee],
  customer: [BUILTIN_EXTENSION_IDS.customer],
};

/** Merge the ids the server announced into the list the page talks to (never drops the built-in one). */
export function rememberExtensionIds(kind: ExtensionKind, fromServer: readonly string[] | null | undefined): void {
  knownExtensionIds[kind] = mergeExtensionIds(knownExtensionIds[kind], fromServer);
}

export type ExtensionMenuStatus = 'none' | 'install' | 'update' | 'installed';

interface ExtensionInstallStore {
  /** What the user menu offers: nothing, install, update, or "installed (vX)". */
  status: ExtensionMenuStatus;
  version: string | null;
  /** Runs the install / update flow (the store link, or the zip download + guide). */
  install: () => void;
  set: (next: Partial<Pick<ExtensionInstallStore, 'status' | 'version' | 'install'>>) => void;
}

/** C-30: the extension state for the user menu, published by the `ExtensionInstall` host. */
export const useExtensionInstallStore = create<ExtensionInstallStore>((set) => ({
  status: 'none',
  version: null,
  install: () => {},
  set: (next) => set(next),
}));

/** localStorage key of "לא עכשיו": per user and per state, so a new version is offered once again. */
export function extensionNoticeKey(userId: string, state: InstallState): string {
  const tag = state.state === 'outdated' ? `outdated:${state.latest}` : state.state;
  return `ote_ext_install_notice_hidden:${userId}:${tag}`;
}

function readHidden(key: string | null): boolean {
  if (!key) return false;
  try { return localStorage.getItem(key) === '1'; } catch { return false; }
}
function writeHidden(key: string) {
  try { localStorage.setItem(key, '1'); } catch { /* private mode: hidden for this visit only */ }
}

type ChromeRuntime = { sendMessage?: (...a: unknown[]) => void; lastError?: unknown };

function sendToExtension(id: string | undefined, message: Record<string, unknown>): Promise<ExtensionPing | null> {
  return new Promise((resolve) => {
    const rt = (window as unknown as { chrome?: { runtime?: ChromeRuntime } }).chrome?.runtime;
    if (!id || !rt?.sendMessage) { resolve(null); return; }
    const timer = window.setTimeout(() => resolve(null), 1500);
    try {
      rt.sendMessage(id, message, (res: ExtensionPing | undefined) => {
        window.clearTimeout(timer);
        // lastError is set when the extension is not installed; reading it silences Chrome's warning.
        void rt.lastError;
        resolve(res ?? null);
      });
    } catch {
      window.clearTimeout(timer);
      resolve(null);
    }
  });
}

/** D-345: asks every known id of this kind; the first one that answers is the installed one (old zip id or store id). */
async function pingExtension(kind: ExtensionKind): Promise<{ id: string | null; ping: ExtensionPing | null }> {
  for (const id of knownExtensionIds[kind]) {
    const ping = await sendToExtension(id, { type: 'ping' });
    if (ping) return { id, ping };
  }
  return { id: null, ping: null };
}

/**
 * Q-DQ-412 (D-313): signing out of the site also signs out the extension (best effort, nothing waits for it).
 * Sent to both flavors; the one that is not installed simply does not answer.
 */
export function notifyExtensionsLogout(): void {
  for (const id of Object.values(knownExtensionIds).flat()) void sendToExtension(id, { type: 'logout' });
}

const GUIDE_STEPS = [1, 2, 3, 4, 5] as const;

function CopyChromeUrl() {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(CHROME_EXTENSIONS_URL);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = CHROME_EXTENSIONS_URL;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch { /* the address is shown, it can be typed */ }
      ta.remove();
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };
  return (
    <span className="mt-2 flex items-center gap-2" dir="ltr">
      <code className="rounded bg-surface-100 px-2 py-1 text-xs" data-testid="extension-chrome-url">{CHROME_EXTENSIONS_URL}</code>
      <button type="button" className="btn btn-secondary btn-sm" onClick={copy} data-testid="extension-copy-url">
        <Copy size={14} aria-hidden="true" />
        {copied ? t('extensionInstall.copied') : t('extensionInstall.copy')}
      </button>
    </span>
  );
}

export default function ExtensionInstall() {
  const { t } = useTranslation();
  const userId = useAuthStore((s) => s.user?.id) ?? null;
  const [state, setState] = useState<InstallState | null>(null);
  const [guideOpen, setGuideOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [hiddenKey, setHiddenKey] = useState<string | null>(null);
  const handedOff = useRef(false);
  const answeredId = useRef<string | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const publish = useExtensionInstallStore((s) => s.set);

  const info = useQuery({
    queryKey: ['extension-info'],
    queryFn: async () => (await api.getExtensionInfo()).data,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const kind = info.data?.kind;
  const latest = info.data?.latest_version;
  const serverIds = info.data?.extension_ids;
  if (kind && serverIds) rememberExtensionIds(kind, serverIds);

  const check = useCallback(async (): Promise<InstallState | null> => {
    if (!kind) return null;
    const { id, ping } = await pingExtension(kind);
    answeredId.current = id;
    const s = installState(ping, kind, latest, userId);
    setState(s);
    return s;
  }, [kind, latest, userId]);

  const handoff = useCallback(async () => {
    if (!kind) return;
    try {
      const { data } = await api.createExtensionHandoff(kind);
      await sendToExtension(answeredId.current ?? knownExtensionIds[kind][0], { type: 'handoff', token: data?.token });
      await check();
    } catch { /* the user can still sign in inside the extension */ }
  }, [kind, check]);

  // Detect on load; while the guide is open keep looking, so the page notices the install by itself.
  useEffect(() => {
    if (!kind) return undefined;
    void check();
    if (!guideOpen) return undefined;
    const id = window.setInterval(() => { void check(); }, 3000);
    return () => window.clearInterval(id);
  }, [kind, check, guideOpen]);

  // Installed but not signed in as this user: connect it, once per page load.
  useEffect(() => {
    if (state && needsHandoff(state) && !handedOff.current) {
      handedOff.current = true;
      void handoff();
    }
  }, [state, handoff]);

  const storeUrl = info.data?.store_url;
  const mode = installMode(info.data);
  const canInstall = mode !== 'none';
  const outdated = state?.state === 'outdated';

  const onInstall = useCallback(async () => {
    if (!kind) return;
    if (mode === 'store' && storeUrl) { window.open(storeUrl, '_blank', 'noopener,noreferrer'); return; }
    setBusy(true);
    try {
      const blob = await api.downloadExtensionZip();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ote-extension-${kind}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      setGuideOpen(true);
    } finally {
      setBusy(false);
    }
  }, [kind, mode, storeUrl]);

  // The user menu's item follows the state (nothing until the server and the extension answered).
  useEffect(() => {
    if (!kind || !info.data || !state) { publish({ status: 'none', version: null }); return; }
    if (state.state === 'installed') { publish({ status: 'installed', version: state.version, install: onInstall }); return; }
    if (!canInstall) { publish({ status: 'none', version: null }); return; }
    publish({ status: state.state === 'outdated' ? 'update' : 'install', version: state.state === 'outdated' ? state.version : null, install: onInstall });
  }, [kind, info.data, state, canInstall, onInstall, publish]);

  // "לא עכשיו" per user and state.
  useEffect(() => {
    setHiddenKey(userId && state ? extensionNoticeKey(userId, state) : null);
  }, [userId, state]);
  const [hidden, setHidden] = useState(false);
  useEffect(() => { setHidden(readHidden(hiddenKey)); }, [hiddenKey]);

  if (!kind || !info.data || !state) return null;

  const guide = (
    <Modal open={guideOpen} onClose={() => setGuideOpen(false)} title={outdated ? t('extensionInstall.guideTitleUpdate') : t('extensionInstall.guideTitle')} sizeClass="max-w-lg">
      {outdated ? (
        <>
          <p className="mb-3 text-sm text-surface-800">{t('extensionInstall.updateBarHow')}</p>
          <ol className="list-decimal space-y-2 ps-5 text-sm text-surface-800">
            <li>{t('extensionInstall.updateStep1')}</li>
            <li>{t('extensionInstall.updateStep2')}</li>
            <li>{t('extensionInstall.updateStep3')}<CopyChromeUrl /></li>
          </ol>
        </>
      ) : (
        <>
          <p className="mb-3 text-sm text-surface-800">{t('extensionInstall.guideIntro')}</p>
          <ol className="space-y-5 text-sm text-surface-800">
            {GUIDE_STEPS.map((n) => (
              <li key={n} className="flex gap-3" data-testid={`extension-step-${n}`}>
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-600 text-xs font-semibold text-white">{n}</span>
                <span className="min-w-0 flex-1">
                  <span className="block font-semibold">{t(`extensionInstall.step${n}Title`)}</span>
                  <span className="block">{t(`extensionInstall.step${n}`)}</span>
                  {n === 2 && <CopyChromeUrl />}
                  <img
                    src={`/extension-guide/step${n}.svg`}
                    alt={t('extensionInstall.stepImageAlt', { n })}
                    className="mt-2 w-full max-w-xs rounded border border-surface-200"
                    loading="lazy"
                    dir="ltr"
                  />
                </span>
              </li>
            ))}
          </ol>
        </>
      )}
      <p className="mt-4 text-xs text-surface-600">{t('extensionInstall.guideAuto')}</p>
      <div className="mt-4 flex justify-end">
        <button type="button" className="btn btn-primary btn-sm" onClick={() => setGuideOpen(false)}>{t('extensionInstall.close')}</button>
      </div>
    </Modal>
  );

  // Installed, or nothing to offer (e.g. the customer extension before it ships), or dismissed: the menu item only.
  if (state.state === 'installed' || !canInstall || hidden || !hiddenKey) return guide;

  const label = outdated ? t('extensionInstall.update') : t('extensionInstall.install');
  const notNow = () => {
    const refocus = !!wrapRef.current?.contains(document.activeElement);
    writeHidden(hiddenKey);
    setHidden(true);
    if (refocus) document.getElementById('main')?.focus({ preventScroll: true });
  };

  return (
    <>
      <div ref={wrapRef} className="px-4 pt-3 sm:px-6 lg:px-8" data-extension-notice={state.state}>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border border-border bg-surface px-3 py-1.5 text-sm text-text" role="status">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <Puzzle size={16} strokeWidth={1.75} className="shrink-0 text-text-2" aria-hidden="true" />
            <p className="min-w-0 truncate" data-testid={showUpdateBar(state, mode) ? 'extension-update-bar' : undefined}>
              {outdated ? t('extensionInstall.updateBar') : t('extensionInstall.noticeText')}
            </p>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => { void onInstall(); }}
              disabled={busy}
              aria-busy={busy || undefined}
              className="btn btn-secondary btn-sm"
              data-testid="extension-install-button"
            >
              {outdated ? <Download size={14} aria-hidden="true" /> : <Puzzle size={14} aria-hidden="true" />}
              {label}
            </button>
            {mode === 'download' && <PendingDecision id="PM-25" />}
            <button type="button" onClick={notNow} className="btn btn-ghost btn-sm">
              {t('extensionInstall.notNow')}
            </button>
          </div>
        </div>
      </div>
      {guide}
    </>
  );
}
